(() => {
  const LABEL = '(?:valokeila(?:n?\\s*kulma)?|valon\\s*kulma|valokulma|leviämiskulma|säteilykulma|sädekulma|avautumiskulma|avauskulma|valonjako|beam\\s*angle|spridningsvinkel|ljusvinkel)';
  const VALUE = '(?<![\\d.,+−-])(\\d{1,3}(?:[.,]\\d+)?)\\s*(?:°\\s*)?(?:[–—-]\\s*(\\d{1,3}(?:[.,]\\d+)?)\\s*)?(?:°|astetta|asteen|degrees?|grader|grad)';
  function readAngle(text, bare = false) {
    const direct = text.match(new RegExp(LABEL + '(?:\\s|:|on|noin|is|är){0,16}' + VALUE, 'i'));
    const before = text.match(new RegExp(VALUE + '\\s*(?::?\\s*n\\s*)?(?:valokeila|valon\\s*kulma|sädekulma)', 'i'));
    const match = direct || before || (bare ? text.match(new RegExp(VALUE, 'i')) : null);
    if (!match) return null;
    const min = Number(match[1].replace(',', '.'));
    const max = match[2] ? Number(match[2].replace(',', '.')) : min;
    return min > 0 && max >= min && max <= 360 ? { min, max } : null;
  }
  function parseRows(rows) {
    const common = [];
    const bySku = {};
    let sku = null;
    let skip = false;
    for (const row of rows) {
      const text = row.text.replace(/\s+/g, ' ').trim();
      if (/^h[2-5]$/.test(row.tag)) {
        skip = /suosittelemat|tutustu myös|recommended|related|rekommenderade/i.test(text);
        const code = text.match(/\b([RG]\d{4,7}|\d{6})\b/i);
        sku = code ? code[1].toUpperCase() : null;
      }
      if (skip) continue;
      const code = text.match(/(?:tuotenumero|tuoteversio|sku|artikelnummer)\s*:?\s*([RG]\d{4,7}|\d{6})\b/i);
      if (code) sku = code[1].toUpperCase();
      const angle = readAngle(text);
      if (!angle) continue;
      const list = sku ? (bySku[sku] ||= []) : common;
      if (!list.some(item => item.min === angle.min && item.max === angle.max)) list.push(angle);
    }
    return { common, bySku };
  }
  function angleForVariant(records, variant) {
    const exact = records.bySku[String(variant.sku || '').trim().toUpperCase()] || [];
    if (exact.length === 1) return exact[0];
    if (exact.length > 1) return null;
    const title = readAngle(variant.title || '', true);
    if (title) return title;
    return records.common.length === 1 ? records.common[0] : null;
  }
  function diameterAt(angle, distance) {
    return Number.isFinite(angle) && Number.isFinite(distance) && angle > 0 && angle < 180 && distance > 0
      ? 2 * distance * Math.tan(angle * Math.PI / 360) : null;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { readAngle, parseRows, angleForVariant, diameterAt };
    return;
  }
  if (customElements.get('nl-beam-angle')) return;
  class BeamAngle extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        this.ready = () => this.init();
        document.addEventListener('DOMContentLoaded', this.ready, { once: true });
      } else this.init();
    }
    init() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.closest('[data-js-product-component]') || this.closest('.shopify-section');
      const description = this.root?.querySelector('.product__description') || this.querySelector('[data-beam-description]')?.content;
      if (!description) return;
      const rows = [...description.querySelectorAll('h2,h3,h4,h5,p,li,tr')]
        .filter(el => !el.matches('li') || !el.querySelector('li'))
        .map(el => ({ tag: el.tagName.toLowerCase(), text: el.textContent }));
      this.records = parseRows(rows);
      this.picker = this.root.querySelector('product-variants');
      this.numberFormat = new Intl.NumberFormat(document.documentElement.lang, { maximumFractionDigits: 2 });
      this.angleInput = this.querySelector('[data-beam-angle]');
      this.distanceInput = this.querySelector('[data-beam-distance]');
      this.details = this.querySelector('details');
      this.onVariant = () => this.syncVariant();
      this.picker?.addEventListener('VARIANT_CHANGE', this.onVariant);
      this.onInput = () => this.update();
      this.addEventListener('input', this.onInput);
      this.syncVariant();
    }
    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.ready);
      this.picker?.removeEventListener('VARIANT_CHANGE', this.onVariant);
      this.removeEventListener('input', this.onInput);
      this.initialized = false;
    }
    syncVariant() {
      let variant;
      if (this.picker) {
        variant = this.picker.currentVariant;
        if (variant === undefined) {
          const data = this.picker.querySelector('[data-js-variant-data]');
          try { variant = data ? JSON.parse(data.textContent) : null; } catch (_) {}
        }
      } else {
        variant = { sku: this.dataset.variantSku, title: this.dataset.variantTitle };
      }
      this.angle = variant ? angleForVariant(this.records, variant) : null;
      this.hidden = !this.angle;
      if (!this.angle) {
        this.details.open = false;
        return;
      }
      this.querySelector('[data-beam-source]').textContent = this.angle.min === this.angle.max
        ? this.numberFormat.format(this.angle.min) + '°'
        : this.numberFormat.format(this.angle.min) + '–' + this.numberFormat.format(this.angle.max) + '°';
      this.querySelector('[data-beam-variant-name]').textContent = variant.sku || '';
      this.angleInput.min = this.angle.min;
      this.angleInput.max = this.angle.max;
      this.angleInput.value = this.angle.min;
      this.angleInput.readOnly = this.angle.min === this.angle.max;
      this.querySelector('[data-beam-range-note]').hidden = this.angle.min === this.angle.max;
      this.update();
    }
    update() {
      const angle = Number(this.angleInput.value);
      const distance = Number(this.distanceInput.value);
      const validAngle = this.angle && Number.isFinite(angle) && angle >= this.angle.min && angle <= this.angle.max;
      const wide = validAngle && angle >= 180;
      this.querySelector('[data-beam-wide]').hidden = !wide;
      this.querySelector('[data-beam-cone-result]').hidden = wide;
      if (!validAngle || !Number.isFinite(distance) || distance <= 0 || !this.distanceInput.validity.valid || !this.angleInput.validity.valid) {
        this.querySelector('[data-beam-error]').hidden = false;
        this.querySelector('[data-beam-output]').textContent = '—';
        return;
      }
      this.querySelector('[data-beam-error]').hidden = true;
      if (wide) return;
      const diameter = diameterAt(angle, distance);
      this.querySelector('[data-beam-output]').textContent = this.numberFormat.format(diameter) + ' m';
      const halfWidth = Math.max(12, Math.min(125, 155 * Math.tan(angle * Math.PI / 360)));
      this.querySelector('[data-beam-cone]').setAttribute('d', 'M150 36 L' + (150 - halfWidth) + ' 192 L' + (150 + halfWidth) + ' 192 Z');
      this.querySelector('[data-beam-floor]').setAttribute('rx', halfWidth);
    }
  }
  customElements.define('nl-beam-angle', BeamAngle);
})();

